import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';

import { KeyValueIsPrimary, KeyValueTypeModel } from '@models/common';
import { ConditionalTemplateGroup } from '@models/module/conditionalTemplateModel';
import { TransitionConditionGroup } from '@models/module/workflowConditionalTransition';
import { WorkflowState } from '@models/module/workflowState';
import { ComplexConditionGroup } from '@module-definition/complex-conditions/complex-conditions-list/complex-conditions.model';
import { TemplateTypeEnum } from '@shared/consts/template-type.enum';
import { HttpService } from '@shared/services';

@Injectable({ providedIn: 'root' })
export class ComplexConditionsListService {
  private httpService = inject(HttpService);

  getConditionalTransitions(transitionId: number) {
    const params = this.httpService.buildHttpParams({ transitionId });
    const url = '_api/TransitionDefinition/GetConditionalTransitions';

    return this.httpService
      .get<TransitionConditionGroup[]>(url, {
        httpOptions: { params },
        useLoader: false,
      })
      .pipe(
        map(result =>
          result.map(
            ({ id, transitionId, stateId, templateId, groupId, position, fieldConditions, screenConditions }) =>
              ({
                id,
                transitionId,
                stateId,
                templateId,
                groupId,
                position,
                fieldConditions,
                screenConditions,
              }) as ComplexConditionGroup,
          ),
        ),
      );
  }

  getOperatorsDict() {
    const url = '_api/TransitionDefinition/OperatorsDict';

    return this.httpService.get<KeyValueTypeModel<number>[]>(url, {
      useLoader: false,
    });
  }

  getConditionalsFieldsDict() {
    const url = '_api/TransitionDefinition/ConditionalsFieldsDict';
    return this.httpService.get<KeyValueTypeModel<number>[]>(url, { useLoader: false });
  }

  getStateDefinition(workflowId: number) {
    const params = this.httpService.buildHttpParams({ workflowId, showDeleted: false });
    const url = '_api/StateDefinition/List';

    return this.httpService.get<WorkflowState[]>(url, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getTemplateDict(entityId: number, screenId: number) {
    const params = this.httpService.buildHttpParams({
      entityId,
      typeName: TemplateTypeEnum.Printout,
      withConditionals: false,
      screenId,
    });
    const url = '_api/TemplateDefinition/TemplatesDict';

    return this.httpService.get<KeyValueIsPrimary<number>[]>(url, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getConditionals(id: number) {
    const params = this.httpService.buildHttpParams({ id });
    const url = '/api/TemplateDefinition/GetConditionals';

    return this.httpService
      .get<ConditionalTemplateGroup[]>(url, {
        httpOptions: { params },
        useLoader: false,
      })
      .pipe(
        map(result =>
          result.map(
            ({ id, groupId, templateId, position, fieldConditions, screenConditions }) =>
              ({
                id,
                groupId,
                templateId,
                position,
                fieldConditions,
                screenConditions,
              }) as ComplexConditionGroup,
          ),
        ),
      );
  }
}
